import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Cake, Calendar, Check, Clock, Copy, Download, EyeOff, Handshake, Newspaper, Sparkles, UserRoundPen, Users, X } from "lucide-react";
import type { ContactRow, TimelineItem, TimelineItemType } from "@hub/shared";
import { Avatar, AvatarStack, Button, EmptyState, IconButton, Menu, Pill, Spinner, BIRTHDAY_DATE, formatDate, relativeTime, toast } from "@/ui";
import { invalidateEngine, useEngineMutation, useEngineQuery } from "@/engine/hooks";
import { getEngine } from "@/engine/transport";
import { useOpenContact } from "@/app/navigation";
import { isTypingTarget } from "@/app/shortcuts";
import { Page } from "@/app/layout/Page";
import { HomeMatchesWidget, PeopleYouKnow } from "@/features/network";
import { TodaysFocusCard } from "./TodaysFocusCard";
import { NeedsAttention } from "./NeedsAttention";
import { localDay, snoozeUntil } from "./focus";
import { ContactChip } from "@/features/people/ContactChip";
import { useContactActions } from "@/features/contact/ContactActionHost";
import s from "../shared.module.css";

type Item = TimelineItem & { contacts: ContactRow[] };

const FEED_PAGE = 12;

/** Folds a run of import rows from the same source on the same day into one row that sums the people. */
export function groupImports<T extends { itemType: string; itemKey: string; itemDate: string; title: string; body: string | null; source?: string | null }>(items: T[]): T[] {
  const out: T[] = [];
  let run: { index: number; source: string; day: string; count: number; people: number } | null = null;
  for (const item of items) {
    if (item.itemType !== "import") {
      out.push(item);
      run = null;
      continue;
    }
    const source = item.source ?? "import";
    const day = item.itemDate.slice(0, 10);
    const people = Number((item.title.match(/(\d[\d,]*)\s+(new\s+)?(people|person)/i)?.[1] ?? "0").replace(/,/g, ""));
    if (run && run.source === source && run.day === day) {
      run.count += 1;
      run.people += people;
      const first = out[run.index];
      out[run.index] = { ...first, title: `Imported ${run.people.toLocaleString()} new people from ${sourceLabel(source)}`, body: `${run.count} imports on this day` } as T;
      continue;
    }
    run = { index: out.length, source, day, count: 1, people };
    out.push(item);
  }
  return out;
}

function sourceLabel(source: string): string {
  const names: Record<string, string> = { apple_contacts: "Apple Contacts", google: "Google", microsoft: "Microsoft 365", csv: "a CSV file", linkedin_csv: "LinkedIn", apple_calendar: "Apple Calendar", ics: "calendar subscription", apple_messages: "Messages", apple_calls: "Phone", apple_mail: "Mail", hub: "Hub" };
  return names[source] ?? source.replace(/_/g, " ");
}

const TYPE_FILTERS: { id: TimelineItemType; label: string }[] = [
  { id: "reconnect", label: "Reconnect" },
  { id: "birthday", label: "Birthdays" },
  { id: "reminder", label: "Reminders" },
  { id: "upcoming_event", label: "Meetings" },
  { id: "news", label: "News" },
  { id: "profile_change", label: "Profile changes" },
  { id: "match", label: "Introductions" },
  { id: "import", label: "Imports" },
  { id: "duplicate", label: "Duplicates" },
];

function iconFor(type: TimelineItemType) {
  switch (type) {
    case "reconnect": return <Users size={16} />;
    case "birthday": return <Cake size={16} />;
    case "reminder": return <Bell size={16} />;
    case "upcoming_event": return <Calendar size={16} />;
    case "news": case "post": return <Newspaper size={16} />;
    case "profile_change": return <UserRoundPen size={16} />;
    case "import": return <Download size={16} />;
    case "duplicate": return <Copy size={16} />;
    case "match": case "introduction": return <Handshake size={16} />;
    default: return <Sparkles size={16} />;
  }
}

function FeedItem({ item, focused, onOpenContact }: { item: Item; focused: boolean; onOpenContact: (id: string) => void }) {
  const navigate = useNavigate();
  const setState = useEngineMutation("setTimelineItemState", { invalidate: ["getTimeline", "getTimelineUnreadCount"] });
  const setContactState = useEngineMutation("setTimelineItemContactState", { invalidate: ["getTimeline"] });
  const snoozeReconnect = useEngineMutation("snoozeReconnect", { invalidate: ["getTimeline", "getReconnectSuggestions"] });
  const markReminder = useEngineMutation("markReminderProcessed", { invalidate: ["getTimeline", "getReminders", "getPendingReminders"] });
  const reasons = (item.metadata.reasons as Record<string, string> | undefined) ?? {};
  const dismiss = () => setState.mutate({ itemKey: item.itemKey, status: "dismissed" });
  return (
    <article className={s.feedItem} data-unseen={!item.seen ? "true" : undefined} data-focused={focused ? "true" : undefined} data-item-key={item.itemKey}>
      <span className={s.feedIcon}>{iconFor(item.itemType)}</span>
      <div className="u-grow" style={{ minWidth: 0 }}>
        <div className="u-row" style={{ gap: 8 }}>
          <span className={s.feedTitle}>{item.title}</span>
          <span className="u-small u-muted" title={item.itemDate}>{item.itemType === "upcoming_event" ? formatDate(item.itemDate, { weekday: "short", month: "short", day: "numeric" }) : item.itemType === "birthday" ? formatDate(item.itemDate, BIRTHDAY_DATE) : relativeTime(item.itemDate)}</span>
          {item.source && <Pill>{sourceLabel(item.source)}</Pill>}
        </div>
        {item.body && <div className={s.feedBody}>{item.body}</div>}
        {item.itemType === "reconnect" ? (
          <div className="u-col" style={{ gap: 6, marginTop: 8 }}>
            {item.contacts.map((c) => (
              <div key={c.contactId} className="u-row" style={{ gap: 10 }}>
                <Avatar name={c.displayName} src={c.avatarUrl} size={32} tier={c.bucket} seed={c.contactId} />
                <div className="u-grow" style={{ minWidth: 0 }}>
                  <button type="button" onClick={() => onOpenContact(c.contactId)} style={{ border: "none", background: "transparent", padding: 0, fontWeight: 600, cursor: "pointer", color: "inherit" }}>{c.displayName}</button>
                  <div className="u-small u-muted u-truncate">{reasons[c.contactId] ?? c.headline ?? ""}</div>
                </div>
                <Button size="sm" variant="outline" onClick={() => onOpenContact(c.contactId)}>Open</Button>
                <Menu
                  align="right"
                  trigger={<IconButton label="Snooze"><Clock size={14} /></IconButton>}
                  items={[
                    { id: "w", label: "Snooze a week", onSelect: () => snoozeReconnect.mutate({ contactId: c.contactId, until: snoozeUntil(7) }) },
                    { id: "m", label: "Snooze a month", onSelect: () => snoozeReconnect.mutate({ contactId: c.contactId, until: snoozeUntil(30) }) },
                    { id: "d", label: "Not now", onSelect: () => setContactState.mutate({ itemKey: item.itemKey, contactId: c.contactId, status: "dismissed" }) },
                  ]}
                />
              </div>
            ))}
          </div>
        ) : item.contacts.length > 0 ? (
          <div className="u-row" style={{ gap: 6, marginTop: 8, flexWrap: "wrap" }}>
            {item.contacts.slice(0, 6).map((c) => <ContactChip key={c.contactId} contactId={c.contactId} row={c} onClick={() => onOpenContact(c.contactId)} />)}
            {item.contacts.length > 6 && <AvatarStack people={item.contacts.slice(6).map((c) => ({ name: c.displayName, src: c.avatarUrl, seed: c.contactId }))} size={22} />}
          </div>
        ) : null}
        {item.itemType === "duplicate" && <Button size="sm" style={{ marginTop: 8 }} onClick={() => navigate("/people/duplicates")}>Review duplicates</Button>}
        {item.itemType === "news" && typeof item.metadata.url === "string" && <a href={item.metadata.url} target="_blank" rel="noreferrer" className="u-small" style={{ display: "inline-block", marginTop: 6 }}>Read the article</a>}
      </div>
      <div className={s.feedActions}>
        {item.itemType === "reminder" && typeof item.metadata.reminderId === "string" && (
          <IconButton label="Mark done" onClick={() => markReminder.mutate({ reminderId: item.metadata.reminderId as string }, { onSuccess: () => toast("Reminder done") })}><Check size={15} /></IconButton>
        )}
        <Menu
          align="right"
          trigger={<IconButton label="Snooze"><Clock size={15} /></IconButton>}
          items={[
            { id: "t", label: "Until tomorrow", onSelect: () => setState.mutate({ itemKey: item.itemKey, status: "snoozed", snoozedUntil: snoozeUntil(1) }) },
            { id: "w", label: "For a week", onSelect: () => setState.mutate({ itemKey: item.itemKey, status: "snoozed", snoozedUntil: snoozeUntil(7) }) },
          ]}
        />
        <IconButton label="Dismiss (e)" onClick={dismiss}><X size={15} /></IconButton>
      </div>
    </article>
  );
}

export function HomePage() {
  const open = useOpenContact();
  const navigate = useNavigate();
  const [types, setTypes] = useState<TimelineItemType[]>([]);
  const [focused, setFocused] = useState(-1);
  const timeline = useEngineQuery("getTimeline", { types: types.length ? types : undefined, limit: 50, offset: 0 }, { placeholderData: (p) => p });
  const birthdays = useEngineQuery("getUpcomingBirthdays", { days: 14 });
  const events = useEngineQuery("getUpcomingEvents", { days: 3 });
  const pending = useEngineQuery("getPendingReminders", {});
  const suggestions = useEngineQuery("getReconnectSuggestions", { localDate: localDay() });
  const stats = useEngineQuery("getContactCount", {});
  const userSettings = useEngineQuery("getUserSettings", undefined);
  const gravatarOn = userSettings.data?.metadata?.gravatar === true;
  const markSeen = useEngineMutation("setTimelineItemsSeen", { invalidate: ["getTimelineUnreadCount"], silent: true });
  const setState = useEngineMutation("setTimelineItemState", { invalidate: ["getTimeline", "getTimelineUnreadCount"] });
  const clearAll = useEngineMutation("dismissTimelineItems", { invalidate: ["getTimeline", "getTimelineUnreadCount"] });
  const seenRef = useRef<Set<string>>(new Set());
  const [showAll, setShowAll] = useState(false);
  const rawItems = timeline.data?.items ?? [];
  // Imports arrive in batches that would fill the feed with near-identical rows; one row per source per day is plenty.
  const items = useMemo(() => groupImports(rawItems), [rawItems]);
  const visibleItems = showAll ? items : items.slice(0, FEED_PAGE);
  const actions = useContactActions(focused >= 0 ? items[focused]?.contacts[0]?.contactId ?? null : null);

  // Look for news about the companies of your closest people a few times a day; the engine rate-limits per company.
  useEffect(() => {
    const key = "hub_news_scan_at";
    let last = 0;
    try {
      last = Number(localStorage.getItem(key) ?? 0);
    } catch {
      last = 0;
    }
    if (Date.now() - last < 6 * 60 * 60 * 1000) return;
    let cancelled = false;
    getEngine()
      .then((e) => e.invoke("scanNews", {}))
      .then((r) => {
        if (cancelled) return;
        try {
          localStorage.setItem(key, String(Date.now()));
        } catch {
          // ignore
        }
        if (r.articles > 0) invalidateEngine(["getTimeline", "getTimelineUnreadCount"]);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  // Opt-in: once a day, look up Gravatar photos (hashed email only) for people without one.
  useEffect(() => {
    if (!gravatarOn) return;
    const key = "hub_avatar_enrich_at";
    let last = 0;
    try {
      last = Number(localStorage.getItem(key) ?? 0);
    } catch {
      last = 0;
    }
    if (Date.now() - last < 24 * 60 * 60 * 1000) return;
    let cancelled = false;
    getEngine()
      .then((e) => e.invoke("enrichAvatars", { limit: 100 }))
      .then((r) => {
        if (cancelled) return;
        try {
          localStorage.setItem(key, String(Date.now()));
        } catch {
          // ignore
        }
        if (r.found > 0) invalidateEngine(["queryContacts", "getContactRows", "getContact"]);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [gravatarOn]);

  // Mark everything on screen as seen shortly after it renders; the unread badge in the nav then clears.
  useEffect(() => {
    const unseen = items.filter((i) => !i.seen && !seenRef.current.has(i.itemKey)).map((i) => i.itemKey);
    if (unseen.length === 0) return;
    const t = setTimeout(() => {
      unseen.forEach((k) => seenRef.current.add(k));
      markSeen.mutate({ itemKeys: unseen });
    }, 1500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (isTypingTarget(e.target) || e.metaKey || e.ctrlKey || document.querySelector("[role='dialog']")) return;
      if (e.key === "ArrowDown") { e.preventDefault(); const next = Math.min(items.length - 1, focused + 1); if (next >= FEED_PAGE) setShowAll(true); setFocused(next); }
      else if (e.key === "ArrowUp") { e.preventDefault(); setFocused((f) => Math.max(0, f - 1)); }
      else if (e.key === "Enter" && focused >= 0 && items[focused]?.contacts[0]) { e.preventDefault(); open(items[focused].contacts[0].contactId); }
      else if (e.key === "e" && focused >= 0 && items[focused]) { e.preventDefault(); setState.mutate({ itemKey: items[focused].itemKey, status: "dismissed" }); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [items, focused, open, setState]);

  useEffect(() => {
    if (focused < 0) return;
    const key = items[focused]?.itemKey;
    if (key) document.querySelector(`[data-item-key="${key}"]`)?.scrollIntoView({ block: "nearest" });
  }, [focused, items]);

  const today = useMemo(() => {
    const now = new Date();
    // Due by the end of today, as the phone counts them; later ones live on the Reminders page.
    const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1).toISOString();
    const dueReminders = (pending.data ?? []).filter((r) => r.fireAt < endOfDay).slice(0, 4);
    // The next two weeks, the window the phone's Home uses, so both show the same people.
    const bdays = (birthdays.data ?? []).filter((b) => b.nextOccurrence).slice(0, 4);
    const allEvents = events.data ?? [];
    return { dueReminders, bdays, events: allEvents.slice(0, 4), moreEvents: Math.max(0, allEvents.length - 4), suggestions: suggestions.data };
  }, [pending.data, birthdays.data, events.data, suggestions.data]);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

  return (
    <Page title="Home" actions={<span className="u-small u-muted">{stats.data !== undefined ? `${stats.data.toLocaleString()} ${stats.data === 1 ? "person" : "people"} in your network` : ""}</span>}>
      <h2 style={{ margin: "4px 0 2px", fontSize: 20, fontWeight: 650 }}>{greeting}</h2>
      <p className="u-muted" style={{ margin: "0 0 18px" }}>What is happening around your network. Hub only watches; it never messages anyone for you.</p>

      {stats.data === 0 && (
        <section className={s.section} data-testid="home-start">
          <div className={s.stat} style={{ borderColor: "var(--accent)" }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Start with your people</div>
            <div className="u-small u-muted" style={{ marginBottom: 10 }}>Hub fills this page once it knows who you know. Bring people in from your Mac or a file, or add one by hand.</div>
            <div className="u-row" style={{ gap: 6, flexWrap: "wrap" }}>
              <Button size="sm" variant="primary" onClick={() => navigate("/settings/integrations")}>Connect Contacts, Messages and Calendar</Button>
              <Button size="sm" onClick={() => navigate("/people/new")}>Add a person</Button>
            </div>
          </div>
        </section>
      )}
      <NeedsAttention />
      <PeopleYouKnow limit={3} className={s.section} />
      {stats.data !== 0 && <HomeMatchesWidget />}

      {stats.data !== 0 && <section className={s.section}>
        <div className="u-row" style={{ justifyContent: "space-between", marginBottom: 8 }}>
          <span style={{ fontWeight: 600 }}>Today</span>
          <span className="u-small u-muted">{formatDate(new Date().toISOString(), { weekday: "long", month: "long", day: "numeric" })}</span>
        </div>
        <div className={s.cardGrid} style={{ gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))" }}>
          <TodaysFocusCard />
          <div className={s.stat}>
            <div className="u-row" style={{ gap: 6, fontWeight: 600, marginBottom: 6 }}><Clock size={14} /> Reconnect</div>
            {suggestions.isLoading && <Spinner />}
            {today.suggestions?.contacts.length === 0 && <div className="u-small u-muted">Nobody is due today. Nice.</div>}
            <div className="u-col" style={{ gap: 6 }}>
              {today.suggestions?.contacts.map((c) => {
                const m = today.suggestions!.members.find((x) => x.contactId === c.contactId);
                return (
                  <div key={c.contactId} className="u-row" style={{ gap: 8, cursor: "pointer" }} onClick={() => open(c.contactId)}>
                    <Avatar name={c.displayName} src={c.avatarUrl} size={28} tier={c.bucket} seed={c.contactId} />
                    <div className="u-grow" style={{ minWidth: 0 }}>
                      <div className="u-truncate" style={{ fontWeight: 500 }}>{c.displayName}</div>
                      <div className="u-small u-muted u-truncate">{m?.reason}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className={s.stat}>
            <div className="u-row" style={{ gap: 6, fontWeight: 600, marginBottom: 6 }}><Cake size={14} /> Birthdays</div>
            {today.bdays.length === 0 && <div className="u-small u-muted">No birthdays in the next two weeks.</div>}
            <div className="u-col" style={{ gap: 6 }}>
              {today.bdays.map((b) => (
                <div key={b.id} className="u-row" style={{ gap: 8, cursor: "pointer" }} onClick={() => open(b.contactId)}>
                  <Avatar name={b.contact.displayName} src={b.contact.avatarUrl} size={28} seed={b.contactId} />
                  <div className="u-grow u-truncate" style={{ fontWeight: 500 }}>{b.contact.displayName}</div>
                  <span className="u-small u-muted">{formatDate(b.nextOccurrence, { ...BIRTHDAY_DATE, weekday: undefined })}</span>
                </div>
              ))}
            </div>
          </div>
          <div className={s.stat}>
            <div className="u-row" style={{ gap: 6, fontWeight: 600, marginBottom: 6 }}><Bell size={14} /> Reminders due</div>
            {today.dueReminders.length === 0 && <div className="u-small u-muted">Nothing due. <button type="button" onClick={() => navigate("/reminders")} style={{ border: "none", background: "none", color: "var(--accent)", cursor: "pointer", padding: 0 }}>See upcoming</button></div>}
            <div className="u-col" style={{ gap: 6 }}>
              {today.dueReminders.map((r) => (
                <div key={r.id} className="u-row" style={{ gap: 8 }}>
                  <div className="u-grow" style={{ minWidth: 0 }}>
                    <div className="u-truncate" style={{ fontWeight: 500 }}>{r.title ?? "Reminder"}</div>
                    {r.contact && <div className="u-small u-muted u-truncate">{r.contact.displayName}</div>}
                  </div>
                  {r.contact && <Button size="sm" variant="ghost" onClick={() => open(r.contact!.contactId)}>Open</Button>}
                </div>
              ))}
            </div>
          </div>
          <div className={s.stat}>
            <div className="u-row" style={{ gap: 6, fontWeight: 600, marginBottom: 6 }}><Calendar size={14} /> Upcoming meetings</div>
            {today.events.length === 0 && <div className="u-small u-muted">No meetings with people you know in the next three days.</div>}
            <div className="u-col" style={{ gap: 6 }}>
              {today.events.map((e) => (
                <div key={e.interaction.id} className="u-row" style={{ gap: 8 }}>
                  <div className="u-grow" style={{ minWidth: 0 }}>
                    <div className="u-truncate" style={{ fontWeight: 500 }}>{e.event?.title ?? "Meeting"}</div>
                    <div className="u-small u-muted">{formatDate(e.interaction.date, { weekday: "short", hour: "numeric", minute: "2-digit" })}</div>
                  </div>
                  <AvatarStack people={e.contacts.map((c) => ({ name: c.displayName, src: c.avatarUrl, seed: c.contactId }))} size={22} />
                </div>
              ))}
              {today.moreEvents > 0 && <div className="u-small u-muted">{today.moreEvents} more in the next three days.</div>}
            </div>
          </div>
        </div>
      </section>}

      {stats.data !== 0 && <section className={s.section}>
        <div className="u-row" style={{ justifyContent: "space-between", marginBottom: 8, flexWrap: "wrap" }}>
          <span style={{ fontWeight: 600 }}>Recent updates</span>
          <div className="u-row" style={{ gap: 4, flexWrap: "wrap" }}>
            <button type="button" className={s.hint} style={{ fontFamily: "var(--font)", background: types.length === 0 ? "var(--accent-soft)" : undefined, color: types.length === 0 ? "var(--accent)" : undefined }} onClick={() => setTypes([])}>All</button>
            {TYPE_FILTERS.map((t) => (
              <button key={t.id} type="button" className={s.hint} style={{ fontFamily: "var(--font)", background: types.includes(t.id) ? "var(--accent-soft)" : undefined, color: types.includes(t.id) ? "var(--accent)" : undefined }} onClick={() => setTypes((ts) => (ts.includes(t.id) ? ts.filter((x) => x !== t.id) : [...ts, t.id]))} aria-pressed={types.includes(t.id)}>
                {t.label}
              </button>
            ))}
            {items.length > 0 && (
              <Button size="sm" variant="ghost" icon={<X size={12} />} onClick={() => { if (window.confirm(`Clear ${timeline.data?.total ?? items.length} updates from the feed?`)) clearAll.mutate({ types: types.length ? types : undefined }); }} title="Dismiss everything in the feed" data-testid="feed-clear-all">
                Clear all
              </Button>
            )}
          </div>
        </div>
        {timeline.isLoading && <div className="u-col">{[0, 1, 2].map((i) => <div key={i} className={s.feedItem}><span className={s.skeleton} style={{ width: 32, height: 32, borderRadius: 8 }} /><div className="u-grow u-col"><span className={s.skeleton} style={{ width: "40%" }} /><span className={s.skeleton} style={{ width: "70%" }} /></div></div>)}</div>}
        {timeline.isSuccess && items.length === 0 && (
          <EmptyState icon={<EyeOff size={26} className="u-muted" />} title="You are caught up">
            Reconnect suggestions, birthdays, reminders, meetings, news and profile changes land here as they happen.
          </EmptyState>
        )}
        {visibleItems.map((item, i) => <FeedItem key={item.itemKey} item={item} focused={i === focused} onOpenContact={(id) => open(id)} />)}
        {!showAll && (items.length > FEED_PAGE || (timeline.data?.total ?? 0) > visibleItems.length) && (
          <div style={{ marginTop: 8 }}><Button size="sm" variant="ghost" onClick={() => setShowAll(true)}>Show {Math.max(items.length - FEED_PAGE, (timeline.data?.total ?? 0) - visibleItems.length)} more</Button></div>
        )}
      </section>}
      {actions.modals}
    </Page>
  );
}
